import { FlexBox, Icon, Tooltip } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber } from 'lib-analytics-ui';
import { useInterval, useUpdate } from 'react-use';

import { useConversationRow } from '../../../utils/useConversationRow';
import { ConversationsTableTimestamp } from '../timestamp/conversationsTableTimestamp';

import styles from './conversationsTableQueuedAt.module.scss';

/** Represents the content of the last queued cell in the conversations table. */
export const ConversationsTableQueuedAt = (): JSX.Element => {
  const rerender = useUpdate();

  const {
    conversation: { queued_at, queue, status, assigned_to },
  } = useConversationRow();

  const longestWaitMilliseconds =
    (queue?.queue_thresholds?.longest_wait ?? 0) * 1000;

  // The queued time isn't cleared when the conversation leaves the queue, so it only counts as waiting while it's open and unassigned.
  const waitingSince =
    status === 'open' && !assigned_to && longestWaitMilliseconds > 0
      ? queued_at
      : undefined;
  const isOverLongestWait = waitingSince
    ? Date.now() - new Date(waitingSince).getTime() > longestWaitMilliseconds
    : false;

  // Checks again on the timestamp's own refresh rate so the alert shows once the limit passes.
  useInterval(rerender, waitingSince && !isOverLongestWait ? 10_000 : null);

  return (
    <FlexBox alignItems="center" gap="200">
      <ConversationsTableTimestamp timestampKey="queued_at" />

      {isOverLongestWait && (
        <Tooltip
          head="Current longest wait in a queue alert"
          content={
            <>
              This conversation has waited longer than the queue’s limit of{' '}
              <b>{formatInstrumentNumber(longestWaitMilliseconds, 'timer')}</b>.
            </>
          }
          className={styles.tooltip}
        >
          <Icon
            name="exclamation-circle-filled"
            size={20}
            color={typography.color.error}
          />
        </Tooltip>
      )}
    </FlexBox>
  );
};
